import React from 'react';
import { BarChart3, Eye, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import StatCard from '../shared/StatCard';
import { publicAnalyticsPath } from '../../lib/analytics';
import { EmptyReport, number, ReportBars, ReportPanel, ReportState } from './ReportPanel';

function safeClarityPage(value) {
  try {
    return (
      publicAnalyticsPath(new URL(value, window.location.origin).pathname) || 'Página restringida'
    );
  } catch {
    return 'Página pública';
  }
}

export function TrafficReport({ report }) {
  const data = report.data;
  return (
    <ReportPanel
      title='Así llegan a tus bonos'
      description='Google Analytics 4 · usuarios, sesiones y vistas del período seleccionado.'
    >
      <ReportState report={report} label='datos de GA4'>
        {!data?.configured ? (
          <EmptyReport>
            Configurá el Property ID y las credenciales de GA4 para ver el tráfico aquí.
          </EmptyReport>
        ) : (
          <div className='grid gap-8 xl:grid-cols-2'>
            <div className='grid content-start gap-3 sm:grid-cols-3'>
              <StatCard label='Usuarios' value={number(data.users)} icon={<Users />} />
              <StatCard label='Sesiones' value={number(data.sessions)} icon={<BarChart3 />} />
              <StatCard label='Vistas de página' value={number(data.pageViews)} icon={<Eye />} />
            </div>
            <div>
              <h3 className='mb-5 text-sm font-semibold'>Fuentes de tráfico</h3>
              {data.sources?.length ? (
                <ReportBars rows={data.sources} valueKey='sessions' unit='sesiones' />
              ) : (
                <EmptyReport>Sin fuentes de tráfico en este período.</EmptyReport>
              )}
            </div>
          </div>
        )}
      </ReportState>
    </ReportPanel>
  );
}

export function ClarityReport({ report }) {
  const data = report.data;
  return (
    <ReportPanel
      title='Una mirada al comportamiento'
      description='Microsoft Clarity · sesiones y páginas de las últimas 72 horas.'
      action={<Badge variant='muted'>72 horas</Badge>}
    >
      <ReportState report={report} label='datos de Clarity'>
        {!data?.configured ? (
          <EmptyReport>
            Configurá el Project ID y el token de Clarity para ver sus métricas aquí.
          </EmptyReport>
        ) : (
          <>
            {data.stale && !data.unavailable && (
              <p
                role='status'
                className='rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm'
              >
                Clarity no respondió. Se muestran datos guardados
                {data.fetchedAt ? ` desde ${new Date(data.fetchedAt).toLocaleString('es-UY')}` : ''}
                .
              </p>
            )}
            {data.unavailable ? (
              <EmptyReport>Clarity no respondió y todavía no hay datos guardados.</EmptyReport>
            ) : (
              <>
                <div className='mb-6 grid gap-3 sm:grid-cols-2'>
                  <StatCard label='Sesiones' value={number(data.sessions)} icon={<BarChart3 />} />
                  <StatCard label='Usuarios' value={number(data.users)} icon={<Users />} />
                </div>
                <h3 className='text-sm font-semibold'>Páginas principales</h3>
                {data.topPages?.length ? (
                  <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                      <caption className='sr-only'>Páginas principales de Clarity</caption>
                      <thead>
                        <tr className='border-b border-border text-muted-foreground'>
                          <th className='py-3 text-left font-medium' scope='col'>
                            Página
                          </th>
                          <th className='text-right font-medium' scope='col'>
                            Sesiones
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.topPages.map((row, index) => (
                          <tr key={index} className='border-b border-border/60 last:border-0'>
                            <td className='break-all py-3 pr-4'>{safeClarityPage(row.name)}</td>
                            <td className='text-right tabular-nums'>{number(row.sessions)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyReport>Aún no hay páginas registradas en Clarity.</EmptyReport>
                )}
              </>
            )}
            <div className='mt-5 flex flex-wrap gap-3'>
              {data.stale && (
                <Button type='button' size='sm' variant='ghost' onClick={report.retry}>
                  Reintentar datos de Clarity
                </Button>
              )}
            </div>
          </>
        )}
      </ReportState>
    </ReportPanel>
  );
}
